import {
  ArrowLeftOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Skeleton, Empty, Modal, message } from "antd";
import {
  useGetBillingHistory,
  BillingRecord,
} from "../hooks/useGetBillingHistory";
import { useState } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

interface Props {
  onBack: () => void;
  currentSubData: any;
}

export const BillingHistoryPage = ({ onBack, currentSubData }: Props) => {
  const {
    data: billingHistory,
    isLoading,
    error,
    refetch,
  } = useGetBillingHistory();
  const [selectedReceipt, setSelectedReceipt] = useState<BillingRecord | null>(
    null,
  );
  const [isModalVisible, setIsModalVisible] = useState(false);

  const receipts = billingHistory || [];

  const totalReceipts = receipts.length;
  const totalSpent2026 = receipts
    .filter((receipt) => {
      const date = new Date(receipt.created_at);
      return date.getFullYear() === 2026;
    })
    .reduce((sum, receipt) => sum + Number(receipt.amount), 0);

  const currentPlan = currentSubData?.package_name || "No Plan";

  const formatCurrency = (
    amount: number | string,
    currency: string = "NGN",
  ) => {
    const numericAmount =
      typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<
      string,
      { bg: string; text: string; label: string }
    > = {
      success: { bg: "bg-green-100", text: "text-green-700", label: "Success" },
      pending: {
        bg: "bg-yellow-100",
        text: "text-yellow-700",
        label: "Pending",
      },
      failed: { bg: "bg-red-100", text: "text-red-700", label: "Failed" },
    };

    const config = statusConfig[status.toLowerCase()] || statusConfig.success;

    return (
      <span
        className={`${config.bg} ${config.text} px-3 py-1 rounded-full text-xs font-medium`}
      >
        {config.label}
      </span>
    );
  };

  const getPlanName = (record: BillingRecord) => {
    if (record.package?.name) {
      return record.package.name;
    }
    if (Array.isArray(record.packages) && record.packages.length > 0) {
      return record.packages[0]?.name || "Unknown Plan";
    }
    return "Unknown Plan";
  };

  const getDescription = (record: any) => {
    const planName = getPlanName(record);
    const duration = record.plan_duration || "monthly";
    const upgradeText = record.upgrade ? "Upgrade" : "Subscription";
    return `${upgradeText} - ${planName} (${duration})`;
  };

  const getInvoiceNumber = (record: any) => {
    return record.trans_ref || `INV-${record.id}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleViewReceipt = (receipt: BillingRecord) => {
    setSelectedReceipt(receipt);
    setIsModalVisible(true);
  };

  const handleDownloadReceipt = (receipt: BillingRecord) => {
    try {
      // jsPDF's built-in fonts lack the ₦ glyph, so show the currency code
      const amount = new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: receipt.currency || "NGN",
        currencyDisplay: "code",
        minimumFractionDigits: 2,
      }).format(Number(receipt.amount));

      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text("Payment Receipt", 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text("Arno", 14, 27);

      autoTable(doc, {
        startY: 35,
        theme: "plain",
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 } },
        body: [
          ["Transaction Reference", receipt.trans_ref || "N/A"],
          ["Payment Reference", receipt.payment_reference || "N/A"],
          ["Date", formatDate(receipt.created_at)],
          ["Description", getDescription(receipt)],
          ["Plan", getPlanName(receipt)],
          ["Duration", receipt.plan_duration || "N/A"],
          ["Type", receipt.upgrade ? "Plan Upgrade" : "Subscription"],
          ["Status", receipt.status],
          ["Amount", amount],
        ],
      });

      doc.save(`receipt-${getInvoiceNumber(receipt)}.pdf`);
    } catch (error) {
      message.error("Failed to download receipt");
    }
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-screen px-8 py-6 bg-gray-50">
        <div className="mb-6">
          <Skeleton active paragraph={{ rows: 1 }} />
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border rounded-lg p-4 bg-white">
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
        <div className="border rounded-xl bg-white overflow-hidden">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen px-8 py-6 bg-gray-50">
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-800 mb-4"
          >
            <ArrowLeftOutlined className="text-xs" />
            <span>Back to Pricing</span>
          </button>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-12 text-center">
          <p className="text-red-600 mb-4">
            Failed to load billing history. Please try again later.
          </p>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Billing History
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            View and download your invoices
          </p>

          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mt-4">
            <button
              onClick={onBack}
              className="flex items-center gap-1 hover:text-gray-800 transition-colors"
            >
              <ArrowLeftOutlined className="text-xs" />
              <span>Pricing</span>
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-900 font-medium">Billing History</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <p className="text-sm text-gray-500">Current Plan</p>
            <h2 className="text-lg font-semibold mt-1">{currentPlan}</h2>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <p className="text-sm text-gray-500">Total Transactions</p>
            <h2 className="text-lg font-semibold mt-1">{totalReceipts}</h2>
          </div>

          <div className="border rounded-lg p-4 bg-white shadow-sm">
            <p className="text-sm text-gray-500">Total Spent (2026)</p>
            <h2 className="text-lg font-semibold mt-1">
              {formatCurrency(totalSpent2026, "NGN")}
            </h2>
          </div>
        </div>

        {/* Table */}
        {receipts.length === 0 ? (
          <div className="border rounded-xl bg-white p-12 text-center shadow-sm">
            <Empty
              description="No billing history found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <button
                onClick={onBack}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                Back to Pricing
              </button>
            </Empty>
          </div>
        ) : (
          <div className="border rounded-xl bg-white overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 border-b">
                  <tr>
                    <th className="text-left px-6 py-4">Date</th>
                    <th className="text-left px-6 py-4">Transaction Ref</th>
                    <th className="text-left px-6 py-4">Description</th>
                    <th className="text-left px-6 py-4">Plan</th>
                    <th className="text-left px-6 py-4">Duration</th>
                    <th className="text-left px-6 py-4">Amount</th>
                    <th className="text-left px-6 py-4">Status</th>
                    <th className="text-left px-6 py-4">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {receipts.map((item, i) => (
                    <tr
                      key={item.id || i}
                      className="border-t hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        {formatDate(item.created_at)}
                      </td>
                      <td className="px-6 py-4 text-gray-500 font-mono text-xs">
                        {getInvoiceNumber(item)}
                      </td>
                      <td className="px-6 py-4">{getDescription(item)}</td>
                      <td className="px-6 py-4">
                        <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
                          {getPlanName(item)}
                        </span>
                      </td>
                      <td className="px-6 py-4 capitalize">
                        {item.plan_duration || "N/A"}
                      </td>
                      <td className="px-6 py-4 font-medium">
                        {formatCurrency(item.amount, item.currency)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(item.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3 text-gray-500">
                          <EyeOutlined
                            className="cursor-pointer hover:text-primary transition-colors"
                            onClick={() => handleViewReceipt(item)}
                          />
                          <DownloadOutlined
                            className="cursor-pointer hover:text-primary transition-colors"
                            onClick={() => handleDownloadReceipt(item)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Receipt Details Modal */}
        <Modal
          title="Transaction Details"
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <button
              key="close"
              onClick={() => setIsModalVisible(false)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>,
            selectedReceipt && (
              <button
                key="download"
                onClick={() => handleDownloadReceipt(selectedReceipt)}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark ml-2"
              >
                Download Receipt
              </button>
            ),
          ]}
        >
          {selectedReceipt && (
            <div className="space-y-3">
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Transaction Reference:</span>
                <span className="font-mono text-xs">
                  {selectedReceipt.trans_ref}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Date:</span>
                <span>{formatDate(selectedReceipt.created_at)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Description:</span>
                <span>{getDescription(selectedReceipt)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Plan:</span>
                <span>{getPlanName(selectedReceipt)}</span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Duration:</span>
                <span className="capitalize">
                  {selectedReceipt.plan_duration || "N/A"}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Amount:</span>
                <span className="font-bold">
                  {formatCurrency(
                    selectedReceipt.amount,
                    selectedReceipt.currency,
                  )}
                </span>
              </div>
              <div className="flex justify-between border-b pb-2">
                <span className="font-medium">Status:</span>
                <span>{getStatusBadge(selectedReceipt.status)}</span>
              </div>
              {selectedReceipt.upgrade === 1 && (
                <div className="flex justify-between border-b pb-2">
                  <span className="font-medium">Type:</span>
                  <span className="text-blue-600">Plan Upgrade</span>
                </div>
              )}
              {selectedReceipt.packages &&
                Array.isArray(selectedReceipt.packages) &&
                selectedReceipt.packages.length > 1 && (
                  <div className="border-b pb-2">
                    <span className="font-medium block mb-2">
                      Package Details:
                    </span>
                    <div className="space-y-1">
                      {selectedReceipt.packages.map((pkg: any, idx: number) => (
                        <div key={idx} className="text-sm text-gray-600">
                          • {pkg.name} - {formatCurrency(pkg.quarterly, "NGN")}
                          {pkg.enterprise_discount &&
                            ` (${pkg.enterprise_discount}% discount)`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};
